import { Button } from '@/components/ui/button';
import { Loader2 } from '@/components/ui/icons';
import { Input } from '@/components/ui/input';
import { AvatarUpdateDialog } from '@/components/auth/AvatarUpdateDialog';
import type { RefObject } from 'react';

interface AccountSectionProps {
  editingName: boolean;
  setEditingName: (v: boolean) => void;
  newName: string;
  setNewName: (v: string) => void;
  nameInputRef: RefObject<HTMLInputElement | null>;
  handleUpdateName: () => void;
  isUpdateLoading: boolean;
  profile: { full_name?: string | null } | null | undefined;
  user: { email?: string | null } | null | undefined;
  handleResetPassword: () => void;
  isResetLoading: boolean;
}

export function AccountSection({
  editingName,
  setEditingName,
  newName,
  setNewName,
  nameInputRef,
  handleUpdateName,
  isUpdateLoading,
  profile,
  user,
  handleResetPassword,
  isResetLoading,
}: AccountSectionProps) {
  return (
    <section className="rounded-xl border border-telekinesis-neutral-800 bg-telekinesis-background-2 p-6">
      <h2 className="mb-5 text-sm font-medium text-telekinesis-neutral-50">
        Account
      </h2>

      <div className="divide-y divide-telekinesis-neutral-800">
        <div className="flex items-center justify-between gap-4 pb-5">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <AvatarUpdateDialog />
            {editingName ? (
              <Input
                ref={nameInputRef}
                value={newName}
                className="h-9 w-full max-w-xs"
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleUpdateName();
                  }
                }}
              />
            ) : (
              <div className="min-w-0 truncate text-sm text-telekinesis-neutral-50">
                {profile?.full_name || user?.email}
              </div>
            )}
          </div>
          {editingName ? (
            <div className="flex flex-shrink-0 items-center gap-2">
              <Button
                onClick={() => handleUpdateName()}
                variant="light"
                disabled={isUpdateLoading}
                className="rounded-full font-light"
              >
                {isUpdateLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Save'
                )}
              </Button>
              <Button
                onClick={() => {
                  setEditingName(false);
                  setNewName(profile?.full_name || '');
                }}
                variant="dark"
                className="rounded-full font-light"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setEditingName(true)}
              variant="dark"
              className="flex-shrink-0 rounded-full font-light"
            >
              Edit
            </Button>
          )}
        </div>

        <div className="py-5">
          <div className="text-sm text-telekinesis-neutral-50">Email</div>
          <div className="mt-0.5 truncate text-xs text-telekinesis-neutral-200">
            {user?.email}
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 pt-5">
          <div className="min-w-0">
            <div className="text-sm text-telekinesis-neutral-50">
              Password
            </div>
            <div className="mt-0.5 text-xs text-telekinesis-neutral-200">
              Send a reset link to your email
            </div>
          </div>
          <Button
            onClick={() => handleResetPassword()}
            disabled={isResetLoading}
            variant="dark"
            className="flex-shrink-0 rounded-full font-light"
          >
            {isResetLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Reset Password'
            )}
          </Button>
        </div>
      </div>
    </section>
  );
}
